import { ref } from 'vue';

import { defineStore } from 'pinia';

import type { Employee } from '@/types/employee';
import type { Organization, OrganizationParent } from '@/types/organization';

import { Result } from '@/classes/result';
import type { Feature } from '@/enums/feature';
import { useAuthStore } from '@/stores/auth';
import { useDialogStore } from '@/stores/components/dialog';
import { useEmployeeStore } from '@/stores/employee';
import { EmployeeStatus } from '@/types/employee';
import { TypeOfOrganization, type AccessibleOrganization, type OrganizationForm } from '@/types/organization';
import type { MoveEmployeeRequest } from '@/types/requests/moveEmployeeRequest';
import type { GetChildrenNameIdPairsResponse } from '@/types/responses/getChildrenNameIdPairsResponse';
import { callApi } from '@/utils/api';
import { handleAxiosError } from '@/utils/error';
import toast from '@/utils/toast';

export const useOrganizationStore = defineStore('organizations', () => {
    const employeeStore = useEmployeeStore();
    const authStore = useAuthStore();

    const organizations = ref<Organization[]>([]);
    const organization = ref<Organization>({} as Organization);

    const assignableOrganizationEmployees = ref<Employee[]>([]);

    async function getChildrenNameIdPairs(organizationId: string): Promise<GetChildrenNameIdPairsResponse> {
        try {
            const result = await callApi({
                method: 'GET',
                endpoint: `organizations/${organizationId}/children/all`
            });

            return result.object[0] as GetChildrenNameIdPairsResponse;
        } catch (error) {
            return handleAxiosError(error, [], 'Onderliggende vestigingen konden niet worden opgehaald');
        }
    }

    async function getAssignableOrganizationEmployees(organizationId: string): Promise<Employee[]> {
        try {
            const result = await callApi({
                method: 'GET',
                endpoint: `organizations/${organizationId}/assignable-employees`
            });

            assignableOrganizationEmployees.value = result.object as Employee[];
            return assignableOrganizationEmployees.value;
        } catch (error) {
            return handleAxiosError(error, [], 'Toewijsbare medewerkers konden niet worden opgehaald');
        }
    }

    async function fetchById(id: string): Promise<Organization | null> {
        try {
            const result = await callApi({
                method: 'GET',
                endpoint: `organizations/${id}`
            });

            organization.value = result.object as Organization;
            return organization.value;
        } catch (error) {
            return handleAxiosError(error, [], 'Bedrijf kon niet opgehaald worden');
        }
    }

    async function get(): Promise<Organization[]> {
        try {
            const result = await callApi({
                method: 'GET',
                endpoint: 'organizations'
            });

            organizations.value = result.object as Organization[];

            return organizations.value;
        } catch (error) {
            return handleAxiosError(error, [], 'Bedrijven konden niet worden opgehaald');
        }
    }

    /**
     * @deprecated use endpoint from OrganizationEmployee instead
     */
    async function updateEmployeeStatus(organizationId: string, employeeId: string, status: EmployeeStatus): Promise<boolean> {
        try {
            await callApi({
                method: 'PUT',
                endpoint: `organizations/${organizationId}/employees/${employeeId}/status/${status}`
            });

            const organizationEmployee = employeeStore.employeesInOrganizations.find((e) => (e.employee as Employee)._id === employeeId);
            if (organizationEmployee) organizationEmployee.status = status;

            const message = status === EmployeeStatus.Active ? 'EmployeeActivated' : 'EmployeeDeactivated';
            toast.success(message);
            return true;
        } catch (error) {
            return handleAxiosError(error, [], 'Kon medewerker status niet wijzigen');
        }
    }

    async function create(organization: OrganizationForm): Promise<Result> {
        try {
            const authStore = useAuthStore();

            const result = await callApi({
                method: 'POST',
                endpoint: 'organizations',
                body: organization
            });

            const createdOrganization = result.object as AccessibleOrganization;
            authStore.accessibleOrganizations?.push(createdOrganization);

            const message = createdOrganization.type === TypeOfOrganization.BRANCH ? 'BranchCreated' : 'CompanyCreated';
            toast.success(message);

            return result;
        } catch (error) {
            return handleAxiosError(error, [], 'Bedrijf kon niet worden aangemaakt');
        }
    }

    async function update(organization: OrganizationForm): Promise<Result> {
        try {
            const result = await callApi({
                method: 'PUT',
                endpoint: `organizations/${organization._id}`,
                body: organization
            });

            const updatedOrganization: Organization = result.object;

            if (organization._id === authStore.currentOrganization?._id) {
                authStore.currentOrganization = updatedOrganization as AccessibleOrganization;
            }

            if (authStore.accessibleOrganizations) {
                const organizationIndex = authStore.accessibleOrganizations.findIndex((o) => o._id === organization._id);
                authStore.accessibleOrganizations[organizationIndex] = updatedOrganization as AccessibleOrganization;
            }

            toast.success('OrganizationUpdated');
            return result;
        } catch (error) {
            return handleAxiosError(error, [], 'Bedrijf kon niet worden bewerkt');
        }
    }

    async function deleteOrganization(organizationId: string): Promise<Result> {
        try {
            const result = await callApi({
                method: 'DELETE',
                endpoint: `organizations/${organizationId}`
            });

            toast.success('OrganizationDeleted');

            return result;
        } catch (error) {
            return handleAxiosError(error, [], 'Bedrijf kon niet worden verwijderd');
        }
    }

    async function getAllParentOrganizations(organizationId: string): Promise<OrganizationParent> {
        try {
            const result = await callApi({
                method: 'GET',
                endpoint: `organizations/${organizationId}/parents`
            });

            return result.object as OrganizationParent;
        } catch (error) {
            return handleAxiosError(error, [], 'Bovenliggende vestigingen konden niet worden opgehaald');
        }
    }

    async function getDirectOrganizationChildren(organizationId: string): Promise<Organization[]> {
        try {
            const result = await callApi({
                method: 'GET',
                endpoint: `organizations/${organizationId}/children`
            });

            return result.object as Organization[];
        } catch (error) {
            return handleAxiosError(error, [], 'Onderliggende bedrijven konden niet worden opgehaald');
        }
    }

    async function moveEmployeeToOrganization(request: MoveEmployeeRequest): Promise<string[]> {
        try {
            const result = await callApi({
                method: 'POST',
                endpoint: 'organizations/move-employee',
                body: request
            });

            const dialogStore = useDialogStore();
            dialogStore.isMoveEmployeeDialogOpen = false;

            const employeeStore = useEmployeeStore();
            employeeStore.employeesInOrganizations = employeeStore.employeesInOrganizations.filter((e) => {
                const employeeId = typeof e.employee === 'object' ? e.employee._id : e.employee;
                return employeeId !== request.employeeId;
            });

            toast.success('EmployeeMoved');
            return result.object;
        } catch (error) {
            return handleAxiosError(error, [], 'Actiepunt kon niet verwijderd worden.');
        }
    }

    async function getFeatures(organizationId: string) {
        try {
            const result = await callApi({
                method: 'GET',
                endpoint: `organizations/${organizationId}/features`
            });

            return result.object as Feature[];
        } catch (error) {
            return handleAxiosError(error, [], 'Features konden niet worden opgehaald');
        }
    }

    return {
        get,
        fetchById,
        updateEmployeeStatus,
        create,
        update,
        deleteOrganization,
        organizations,
        getAllParentOrganizations,
        getChildrenNameIdPairs,
        getDirectOrganizationChildren,
        organization,
        assignableOrganizationEmployees,
        getAssignableOrganizationEmployees,
        moveEmployeeToOrganization,
        getFeatures
    };
});
